import { getAppConfig } from './appConfigRef';

/** Dev bundler origin when `window` is unavailable (tests, SSR). */
export function getDevAppOrigin(devAppOrigin?: string): string {
  return devAppOrigin ?? 'http://localhost:3000';
}

export function getCurrentAppOrigin(devAppOrigin?: string): string {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  return getDevAppOrigin(devAppOrigin);
}

/**
 * OAuth callback URL sent as `redirect_uri` on `/login` and `/auth/handoff/redeem`.
 *
 * Must be registered for this `client_id` (Platform → Client apps). On dev/sandbox the
 * seeded `http://localhost/loginok` row matches any port on the same host and path.
 */
export function getLoginRedirectUri(): string {
  const config = getAppConfig();

  if (config.loginRedirectUri) {
    return config.loginRedirectUri;
  }

  const devOrigin = config.devAppOrigin;
  return `${getCurrentAppOrigin(devOrigin)}${config.loginCallbackPath}`;
}

export function buildElvaLoginUrl(params: { challenge: string; state: string }): string {
  const config = getAppConfig();
  const redirectUri = getLoginRedirectUri();

  if (config.accountDomain.replace(/\/$/, '') === getCurrentAppOrigin(config.devAppOrigin)) {
    throw new Error(
      `@elva/platform: VITE_ACCOUNT_DOMAIN points at this app (${config.accountDomain}). ` +
        'Set VITE_ACCOUNT_DOMAIN to the account frontend host, not this microapp.',
    );
  }

  const search = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: redirectUri,
    code_challenge: params.challenge,
    code_challenge_method: 'S256',
    state: params.state,
  });

  return `${config.accountDomain.replace(/\/$/, '')}${config.accountLoginPath}?${search.toString()}`;
}

/** Profile, password, people, locations and plans all live in the account app. */
export function getAccountAppUrl(path = '/'): string {
  const config = getAppConfig();
  return `${config.accountDomain}${path.startsWith('/') ? path : `/${path}`}`;
}

/** @deprecated Use `getCurrentAppOrigin`. */
export function getCurrentAppUrl(devAppUrl?: string): string {
  return getCurrentAppOrigin(devAppUrl);
}

/** @deprecated Use `buildElvaLoginUrl`. */
export function getElvaLoginUrl(params: { challenge: string; state: string }): string {
  return buildElvaLoginUrl(params);
}
